import 'moment/locale/ja';
import { Button, Input, Pagination, Select, Spin, Table } from 'antd';
import { useMemo, useState } from 'react';
import searchIcon from 'src/assets/images/search-icon.png';
import { useNavigate } from 'react-router-dom';
import type { ColumnsType } from 'antd/es/table';
import { NOTIFICATION_STATUS, pageSizeList } from 'src/assets/constants';
import { getClassStatusNotify, getIndexTable, handleGetStatusHighlight, handleGetStatusNotify } from 'src/utils';
import iconSelect from 'src/assets/images/arrow-down-2.png';
import TableNoteNoData from 'src/components/common/tableNoteNoData';
import IconAdd from 'src/assets/icon/IconAdd';
import { useListNotification } from 'src/api/useListNotification';
import moment from 'moment';
import { decodeUrl } from 'src/utils/decodeUrl';
import ReactImageFallback from 'react-image-fallback';
import noImage from 'src/assets/images/no-image.png';

interface DataType {
  key: string;
  name: string;
  location: string;
  updated_at: string;
}

const columns: ColumnsType<DataType> = [
  {
    title: '番号',
    key: 'index',
    dataIndex: 'index',
    width: 100,
  },
  {
    title: '画像サムネール',
    dataIndex: 'image_thumbnail_url',
    key: 'image_thumbnail_url',
    width: 250,
  },
  {
    title: 'タイトル',
    dataIndex: 'title',
    key: 'title',
    width: 376,
  },
  {
    title: '表示/非表示',
    key: 'status',
    dataIndex: 'status',
    width: 175,
  },
  {
    title: 'ハイライト',
    key: 'highlight',
    dataIndex: 'highlight',
    width: 120,
  },
  {
    title: '作成日時',
    key: 'created_at',
    dataIndex: 'created_at',
    width: 200,
  },
];

const NotificationListComponent = () => {
  const history = useNavigate();
  const [keyword, setKeyword] = useState<string>('');
  const [keywordSearch, setKeywordSearch] = useState<string>('');
  const [status, setStatus] = useState<number | undefined>();
  const [page, setPage] = useState<number>(1);
  const [sizePage, setSizePage] = useState<number>(10);

  const { data: notificationListData, isLoading } = useListNotification(keywordSearch, page, sizePage, status);

  const dataSource = useMemo(() => {
    if (notificationListData) {
      return notificationListData?.records?.data?.map((notification: any, index: any) => ({
        ...notification,
        key: notification?.id,
        index: <span className="line-1">{getIndexTable(page, sizePage, index)}</span>,
        image_thumbnail_url: (
          <span className="inline-block">
            <ReactImageFallback
              src={decodeUrl(notification?.image_thumbnail_url)}
              fallbackImage={noImage}
              initialImage={<Spin />}
              alt=""
              className="image-in-table"
            />
          </span>
        ),
        title: (
          <span
            onClick={() => handleGoToNotifyDetail(notification?.id)}
            className="line-1 break-all column-redirect cursor-pointer"
            style={{ display: '-webkit-inline-box' }}
          >
            {notification?.title}
          </span>
        ),
        status: (
          <span className={`notify-request-status inline-block ${getClassStatusNotify(notification?.status)}`}>
            {handleGetStatusNotify(notification?.status)}
          </span>
        ),
        highlight: (
          <span className="line-1 break-all font-bold">{handleGetStatusHighlight(notification?.highlight)}</span>
        ),
        created_at: (
          <span className="line-1 break-all">
            {notification?.created_at ? moment(notification?.created_at).utc().format('YYYY/MM/DD') : ''}
          </span>
        ),
      }));
    }
    return [];
  }, [notificationListData]);

  const handleChangeKeyword = (value: string) => {
    setKeyword(value);
  };

  const iconSearch = () => {
    return (
      <div className="p-[5px] cursor-pointer" onClick={handleSearch}>
        <img src={searchIcon} alt="" />
      </div>
    );
  };

  const handleChangeSizePage = (value: number) => {
    setPage(1);
    setSizePage(value);
  };

  const handleChangePage = (page: number) => {
    setPage(page);
  };

  const handleSearch = () => {
    setPage(1);
    setKeywordSearch(keyword?.trim());
    setKeyword(keyword?.trim());
  };

  const renderSelectIcon = () => {
    return <img src={iconSelect} alt="iconSelect" />;
  };

  const handleChangeStatus = (status: number) => {
    setPage(1);
    setStatus(status);
    setKeywordSearch(keyword?.trim());
    setKeyword(keyword?.trim());
  };

  const handleGoToCreatePage = () => {
    history('/notification/create');
  };

  const handleGoToNotifyDetail = (id: number | undefined) => {
    history(`/notification/${id}`);
  };

  return (
    <div className="page-content company-page">
      <div className="mb-[16px]">
        <h2 className="page-content-title border-bottom-title">お知らせ一覧</h2>
      </div>
      <div className="page-content-table">
        <div className="flex justify-between items-center p-[16px]">
          <div className="flex items-center">
            <Input
              onPressEnter={handleSearch}
              className="input-search mr-[8px] min-w-[423px]"
              placeholder="タイトルを検索してください"
              suffix={iconSearch()}
              onChange={(e) => handleChangeKeyword(e.target.value)}
              value={keyword}
            />
            <Select
              options={NOTIFICATION_STATUS}
              placeholder="表示/非表示"
              className="company-select h-[40px] w-[200px]"
              allowClear={true}
              onChange={(value: number) => handleChangeStatus(value)}
              suffixIcon={renderSelectIcon()}
            />
          </div>
          <Button className="button-add max-h-[40px] w-[128px]" onClick={handleGoToCreatePage}>
            <IconAdd className="mr-[10px]" />
            追加
          </Button>
        </div>

        <Table
          columns={columns}
          dataSource={dataSource}
          pagination={false}
          loading={isLoading}
          locale={{
            emptyText: <TableNoteNoData />,
          }}
        />

        <div className="py-[20px] px-[12px] flex justify-between items-center">
          <div className="pagination-info">
            {notificationListData?.limitFrom} - {notificationListData?.limitTo} 件表示/全{' '}
            {notificationListData?.totalRow} 件中
          </div>
          <div className="flex">
            <Pagination
              current={page}
              total={notificationListData?.totalRow}
              className="pagination-main mr-[20px]"
              showSizeChanger={false}
              pageSize={sizePage}
              onChange={(page) => handleChangePage(page)}
            />
            <Select
              defaultValue={10}
              style={{ width: 153 }}
              className="pagination-options"
              onChange={(e) => handleChangeSizePage(e)}
              options={pageSizeList}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationListComponent;
